// The collaborative marker on a forecast-manager card.
//
// Absolutely positioned in the card's top-right corner, which every row variant
// can host because their roots are already positioned.
//
// A BARE ACCENT GLYPH, no tile: the grey chip read as a filled control sitting on
// the card, where this should be a mark in the corner. Both states share the
// colour and the ARROW DIRECTION tells them apart (see COLLAB_BADGE_STYLES).
//
// ── WHY THIS DRAWS ITS OWN TOOLTIP ───────────────────────────────────────────
//
// It used to rely on the native `title` attribute, and the hover text never
// appeared. Two reasons, and the first alone was fatal: the badge carried
// `pointerEvents: none` (so the browser never hit-tested it, and a title cannot
// fire on an element the pointer never reaches), and even with that fixed a
// native tooltip waits about a second, renders in the OS style, and cannot be
// verified from a test. So the label is a real element, shown on hover by CSS.
//
// `pointer-events` stays ON for the badge — that is what makes :hover fire — and
// nothing is stopped, so the click still bubbles to the card and selects the row.
// The TOOLTIP itself keeps `pointer-events: none` so it can never sit between the
// cursor and the card.
import { C } from "../lib/colors.js";
import { collabBadge, collabBadgeTitle } from "../lib/forecast/collabBadge.js";
import { CollabIcon } from "./Icons.jsx";
import { ui } from "../lib/telemetry/instrument.js";

/**
 * @param {{f: object|null|undefined}} props a forecast row; renders nothing when
 *   the row is not collaborative, which is the common case.
 */
export default function CollabBadge({ f, onOpenShare }) {
  const badge = collabBadge(f);
  if (!badge) return null;
  const label = collabBadgeTitle(f);
  // A CONTROL only when a handler is wired. App passes one only where the share
  // dialog is actually reachable, so the badge never offers a click that goes
  // nowhere — the rule the rest of this card follows.
  const clickable = typeof onOpenShare === "function";
  return (
    <div
      className="collab-badge"
      data-collab-badge={badge.kind}
      {...(clickable && ui("forecast-manager.collab-badge", f.id))}
      data-telemetry-redact=""
      aria-label={clickable ? `${label} — open sharing` : label}
      role={clickable ? "button" : "img"}
      tabIndex={clickable ? 0 : undefined}
      onClick={
        clickable
          ? (e) => {
              // NOT stopped. The click bubbles to the card, which selects the row
              // — and the dialog reads the ACTIVE forecast, so it must. React
              // batches both state updates, so the dialog opens against the row
              // just selected rather than the previously active one.
              onOpenShare(f, e);
            }
          : undefined
      }
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              onOpenShare(f, e);
            }
          : undefined
      }
      style={{
        position: "absolute",
        top: 5,
        right: 8,
        zIndex: 3,
        color: C.purpleL || C.purple,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: clickable ? "pointer" : "inherit",
      }}
    >
      <CollabIcon size={24} direction={badge.direction} />
      <span className="collab-badge__tip" data-collab-tip="">
        {label}
      </span>
    </div>
  );
}
